import { Box, Grid2, Modal, Typography, Button } from "@mui/material";
import { Check, X } from "phosphor-react";
import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { fget, fpost } from "../../../API/callsAPI";
import commonStyles from "../../../Utils/commonStyles";
import CustomDropdown from "../../Utilities/CustomDropdown/CustomDropdown.component";
import { trackablesList } from "../../utils";
import CreateOrEditCustomFieldModal from "./CreateOrEditCustomFieldModal";
import SingleFieldView from "./SingleFieldView";
import SingleTableView from "./SingleTableView";
import ErrorOrSuccessMessage from "../../Utilities/ErrorOrSuccessMessage/ErrorOrSuccessMessage.component";
import { mapFieldsFromConfig } from "../../Utilities/CustomTableForAllTrackables/fieldMapper.js";

const FieldTemplatesSettingsOverview = ({ projectId }) => {
  const commonClasses = commonStyles();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [customFields, setCustomFields] = useState([]);
  const [defaultFields, setDefaultFields] = useState([]);
  const [isCreateNewFieldModalOpen, setIsCreateNewFieldModalOpen] =
    useState(false);
  const [selectedTrackable, setSelectedTrackable] = useState(
    parseInt(searchParams.get("selectedTrackable")) || 12
  );
  const [selectedFieldsList, setSelectedFieldsList] = useState([]);
  const [snackValue, setSnackValue] = useState({
    isOpen: false,
    message: "",
    isError: false,
  });

  const handleFieldClick = (field) => {
    if (selectedFieldsList?.length) {
      const isExist = selectedFieldsList?.some(
        (sin) => sin.name === field.name
      );
      if (isExist) {
        setSelectedFieldsList(
          selectedFieldsList.filter((sin) => sin.name !== field.name)
        );
      } else {
        setSelectedFieldsList([...selectedFieldsList, field]);
      }
    } else {
      setSelectedFieldsList([field]);
    }
  };

  const handleGetTemplates = async () => {
    try {
      const res = await fget({
        url: `/trackables/trackable-field/?project=${projectId}&trackable=${selectedTrackable}`,
      });
      setCustomFields(res?.data?.custom_fields);
      setDefaultFields(res?.data?.default_fields);
    } catch (error) {
      console.log(error);
    }
  };

  const getSelectedFields = async () => {
    try {
      const res = await fget({
        url: `trackables/trackable-config/?project=${projectId}&trackable=${selectedTrackable}`,
      });
      const resdata = mapFieldsFromConfig(
        res?.data?.config,
        customFields,
        defaultFields,
      );
      setSelectedFieldsList(resdata);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set("selectedTrackable", selectedTrackable);
    history.replace({ search: params.toString() });
  }, [selectedTrackable]);

  useEffect(() => {
    handleGetTemplates();
  }, [projectId, selectedTrackable]);

  useEffect(() => {
    getSelectedFields();
  }, [customFields, defaultFields]);

  const handleReorderFields = (newOrder) => {
    setSelectedFieldsList(newOrder);
  };

  const handleConfigureFields = async () => {
    const config = {};
    selectedFieldsList.forEach((item, index) => {
      config[index + 1] = item.name;
    });

    const data = {
      project: projectId,
      trackable: selectedTrackable,
      config: config,
    };

    try {
      const res = await fpost({
        url: `/trackables/trackable-config/`,
        data: data,
      });

      if (res.status === 200 || res.status === 201) {
        setSnackValue({
          isOpen: true,
          message: "Fields configured successfully",
          isError: false,
        });
      }
    } catch (error) {
      console.log(error);
      setSnackValue({
        isOpen: true,
        message: "Failed to configure fields",
        isError: true,
      });
    }
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: "24px !important",
          fontWeight: 500,
          mb: 1,
        }}
      >
        Field Templates
      </Typography>
      <Box className={commonClasses.flexBetween}>
        <Box sx={{ minWidth: "150px" }}>
          <CustomDropdown
            value={selectedTrackable}
            updateValue={(val) => {
              setSelectedTrackable(val);
              setSelectedFieldsList([]);
            }}
            options={trackablesList}
            showNone={false}
          />
        </Box>
        <Box className={commonClasses.flexAndGap8Px}>
          <Button
            variant="contained"
            startIcon={<Check />}
            onClick={handleConfigureFields}
            sx={{
              backgroundColor: "#6C65D9",
              textTransform: "none",
              borderRadius: "10px",
              padding: "10px 20px",
              color: "white !important",
            }}
          >
            Save Changes
          </Button>
          <Button
            onClick={() => setSelectedFieldsList([])}
            variant="outlined"
            startIcon={<X />}
            sx={{
              borderColor: "#6C65D9 !important",
              textTransform: "none",
              borderRadius: "10px",
              padding: "10px 20px",
              color: "white",
            }}
          >
            Discard Changes
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          border: "1px solid #606479",
          padding: "10px",
          borderRadius: "10px",
          marginTop: "16px",
        }}
      >
        <Typography
          sx={{
            fontSize: "20px !important",
            fontWeight: 600,
          }}
        >
          {
            trackablesList?.find((trac) => trac.value === selectedTrackable)
              ?.title
          }
        </Typography>
        <Grid2 container spacing={2}>
          <Grid2 size={6}>
            <SingleFieldView
              title="Default Fields"
              showAddIcon={false}
              fields={defaultFields}
              type="default"
              onFieldClick={handleFieldClick}
              selectedFields={selectedFieldsList}
            />
          </Grid2>
          <Grid2 size={6}>
            <SingleFieldView
              title="Custom Fields"
              showAddIcon={true}
              handleAddNew={() => setIsCreateNewFieldModalOpen(true)}
              fields={customFields}
              type="custom"
              onFieldClick={handleFieldClick}
              selectedFields={selectedFieldsList}
              handleGetTemplates={handleGetTemplates}
            />
          </Grid2>
        </Grid2>
        <SingleTableView
          items={selectedFieldsList}
          onReorder={handleReorderFields}
        />
      </Box>

      <Modal
        open={isCreateNewFieldModalOpen}
        onClose={() => setIsCreateNewFieldModalOpen(false)}
        className={commonClasses.flexCenter}
      >
        <CreateOrEditCustomFieldModal
          type="new"
          handleClose={() => setIsCreateNewFieldModalOpen(false)}
          projectId={projectId}
          updateRefreshCounter={handleGetTemplates}
          selectedTrackable={selectedTrackable}
        />
      </Modal>
      <ErrorOrSuccessMessage snackValue={snackValue} />
    </Box>
  );
};

export default FieldTemplatesSettingsOverview;
